import { useMemo, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowDown, ArrowLeft, Flag, GitBranch, KeyRound, Mail, Play, ShieldAlert, Tag, Timer, Zap } from "lucide-react";
import { api, qs } from "@/lib/api";
import { formatDateTime, formatInt, formatPercent } from "@/lib/format";
import { cn } from "@/lib/utils";
import { BrandMark } from "@/components/BrandMark";
import { Empty } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { AUTOMATION_STATUS, type AutomationSummary } from "./AutomationsPage";
import { STEP_TYPES, stepRows } from "./flow";
import { LogTable } from "./log";
import { EmailLabelLine, useEmailLabels } from "./StepEmailLabel";
import type { AutomationDetail, CompareDecision, CompareOut, LogEntry } from "./types";

type Detail = AutomationDetail & { automation: AutomationSummary & { created_by?: string } };
interface Enrollment {
  id: number;
  contact_id: number;
  contact_name: string;
  status: string;
  shadow: boolean;
  step_key: string;
  step_label: string;
  next_run_at: string | null;
  entered_at: string;
  exit_reason: string | null;
}

const stepIcon: Record<string, typeof Mail> = { email: Mail, sign_in_link: KeyRound, wait: Timer, wait_until: Timer, time_window: Timer, tag_add: Tag, tag_remove: Tag, branch: GitBranch, goal: Flag };

/** One automation: its steps with how many people are at each, the people in it, versions, and the shadow comparison. */
export function AutomationDetailPage() {
  const { automationId } = useParams({ from: "/automations/$automationId" });
  const id = Number(automationId);
  const q = useQuery({ queryKey: ["automation", id], queryFn: () => api<Detail>(`/api/v1/automations/${id}`) });
  const [step, setStep] = useState<string | undefined>();
  if (q.isLoading) return <Skeleton className="h-96" />;
  if (!q.data) return <p className="text-sm text-[var(--color-destructive)]">{q.error?.message ?? "Automation not found."}</p>;
  const d = q.data;
  const a = d.automation;
  return (
    <div className="mx-auto max-w-[1300px] space-y-3">
      <Link to="/automations" className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
        <ArrowLeft className="size-4" /> Automations
      </Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">{a.name}</h1>
        {a.brand_id && <BrandMark id={a.brand_id} />}
        <Badge variant={AUTOMATION_STATUS[a.status]?.variant ?? "muted"} title={AUTOMATION_STATUS[a.status]?.help}>
          {AUTOMATION_STATUS[a.status]?.label ?? a.status}
        </Badge>
        {a.keap_campaign_id && <span className="text-xs text-[var(--color-muted-foreground)]">replaces Keap campaign #{a.keap_campaign_id}</span>}
        <StatusActions a={a} hasDraft={!!d.draft} />
      </div>
      {a.description && <p className="text-sm text-[var(--color-muted-foreground)]">{a.description}</p>}
      <p className="text-xs text-[var(--color-muted-foreground)]">{AUTOMATION_STATUS[a.status]?.help}</p>

      <div className="grid gap-3 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <Flow d={d} selected={step} onSelect={(k) => setStep((s) => (s === k ? undefined : k))} />
        <div className="space-y-3">
          {a.status === "shadow" && <ShadowCompare id={id} />}
          <People id={id} step={step} onClear={() => setStep(undefined)} />
          <Activity id={id} />
          <Versions d={d} />
        </div>
      </div>
    </div>
  );
}

function Flow({ d, selected, onSelect }: { d: Detail; selected?: string; onSelect: (key: string) => void }) {
  const rows = useMemo(() => stepRows(d), [d]);
  const labels = useEmailLabels(d.automation.id);
  const nodes = d.graph?.nodes ?? [];
  const triggers = nodes.filter((n) => n.kind === "trigger");
  const goals = nodes.filter((n) => n.kind === "goal");
  return (
    <Card>
      <CardHeader>
        <CardTitle>How it runs</CardTitle>
        <span className="text-xs text-[var(--color-muted-foreground)]">click a step to list the people at it</span>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <section>
          <h4 className="mb-1 text-xs font-semibold text-[var(--color-muted-foreground)]">Starts when</h4>
          {triggers.length === 0 && <p className="text-[var(--color-muted-foreground)]">Only when someone is started by hand.</p>}
          {triggers.map((t) => (
            <div key={t.id} className="flex items-start gap-2">
              <Zap className="mt-0.5 size-3.5 shrink-0 text-[var(--color-warning)]" />
              <span>
                {t.label}
                {t.details && <span className="block text-xs text-[var(--color-muted-foreground)]">{t.details}</span>}
              </span>
            </div>
          ))}
        </section>
        <ol className="space-y-1">
          {rows.map((row, i) => {
            const Icon = stepIcon[row.type] ?? Play;
            return (
              <li key={row.key}>
                {i > 0 && <ArrowDown className="mx-auto mb-1 size-3.5 text-[var(--color-muted-foreground)]" aria-hidden />}
                <button
                  onClick={() => onSelect(row.key)}
                  className={cn(
                    "flex w-full items-start gap-2 rounded-md border px-3 py-2 text-left hover:bg-[var(--color-muted)]",
                    selected === row.key && "border-[var(--color-primary)] bg-[var(--color-accent)]",
                  )}
                  aria-pressed={selected === row.key}
                >
                  <Icon className="mt-0.5 size-4 shrink-0 text-[var(--color-muted-foreground)]" />
                  <span className="min-w-0 flex-1">
                    <span className="block">
                      <span className="num mr-1.5 text-xs text-[var(--color-muted-foreground)]">{row.number}</span>
                      {!row.label.toLowerCase().startsWith((STEP_TYPES[row.type] ?? row.type).toLowerCase()) && (
                        <span className="mr-1.5 text-xs text-[var(--color-muted-foreground)]">{STEP_TYPES[row.type] ?? row.type}</span>
                      )}
                      {row.label}
                    </span>
                    {row.startsHere.length > 0 && <span className="block text-xs text-[var(--color-warning-fg)]">starts here: {row.startsHere.join("; ")}</span>}
                    <span className="mt-0.5 block text-xs text-[var(--color-muted-foreground)]">then: {row.then}</span>
                    {row.note && <span className="block text-xs italic text-[var(--color-muted-foreground)]">{row.note}</span>}
                  </span>
                  {(row.people > 0 || row.shadowPeople > 0) && (
                    <span className="flex shrink-0 flex-col items-end gap-0.5">
                      {row.people > 0 && <Badge variant="success">{formatInt(row.people)} here</Badge>}
                      {row.shadowPeople > 0 && <Badge variant="muted">{formatInt(row.shadowPeople)} in shadow</Badge>}
                    </span>
                  )}
                </button>
                {labels.data?.[row.key] && <EmailLabelLine automationId={d.automation.id} step={labels.data[row.key]} />}
              </li>
            );
          })}
        </ol>
        {goals.length > 0 && (
          <section>
            <h4 className="mb-1 text-xs font-semibold text-[var(--color-muted-foreground)]">Goals (reaching one ends or moves the run)</h4>
            {goals.map((g) => (
              <div key={g.id} className="flex items-start gap-2">
                <Flag className="mt-0.5 size-3.5 shrink-0 text-[var(--color-success)]" />
                <span>
                  {g.label}
                  {g.details && <span className="block text-xs text-[var(--color-muted-foreground)]">{g.details}</span>}
                </span>
              </div>
            ))}
          </section>
        )}
        {Object.keys(d.status_counts).length > 0 && (
          <p className="border-t pt-2 text-xs text-[var(--color-muted-foreground)]">
            All runs: {Object.entries(d.status_counts).map(([k, v]) => `${formatInt(v)} ${k.replace(/_/g, " ")}`).join(", ")}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function People({ id, step, onClear }: { id: number; step?: string; onClear: () => void }) {
  const q = useInfiniteQuery({
    queryKey: ["automation-people", id, step],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => api<{ enrollments: Enrollment[]; next_before_id?: number }>(`/api/v1/automations/${id}/enrollments` + qs({ step, before_id: pageParam || undefined, limit: 50 })),
    getNextPageParam: (l) => l.next_before_id || undefined,
  });
  const rows = q.data?.pages.flatMap((p) => p.enrollments) ?? [];
  return (
    <Card className="overflow-x-auto">
      <CardHeader>
        <CardTitle>People {step ? `at step “${step}”` : "in it"}</CardTitle>
        {step && (
          <button className="text-xs text-[var(--color-primary)] hover:underline" onClick={onClear}>
            Everyone
          </button>
        )}
      </CardHeader>
      {q.isLoading && <Skeleton className="m-3 h-24" />}
      {!q.isLoading && rows.length === 0 && <Empty title="Nobody here." />}
      {rows.length > 0 && (
        <Table>
          <THead>
            <tr>
              <TH>Person</TH>
              <TH>At</TH>
              <TH>Status</TH>
              <TH>Next (MT)</TH>
              <TH>Started</TH>
            </tr>
          </THead>
          <tbody>
            {rows.map((r) => (
              <TR key={r.id}>
                <TD className="max-w-[12rem]">
                  <Link to="/contacts/$contactId" params={{ contactId: String(r.contact_id) }} className="block truncate text-[var(--color-primary)] hover:underline">
                    {r.contact_name || `#${r.contact_id}`}
                  </Link>
                </TD>
                <TD className="max-w-[20rem]">
                  <Link
                    to="/automations/runs/$runId"
                    params={{ runId: String(r.id) }}
                    className="line-clamp-2 text-[var(--color-primary)] hover:underline"
                    title={`${r.step_label || r.step_key}. Open this run: step log, pause, retry, take out`}
                  >
                    {r.step_label || r.step_key}
                  </Link>
                </TD>
                <TD>
                  <span className="flex gap-1">
                    <Badge variant={r.status === "active" || r.status === "waiting" ? "success" : "muted"}>{r.status.replace(/_/g, " ")}</Badge>
                    {r.shadow && <Badge variant="muted">shadow</Badge>}
                  </span>
                  {r.exit_reason && <div className="text-xs text-[var(--color-muted-foreground)]">{r.exit_reason}</div>}
                </TD>
                <TD className="whitespace-nowrap">{r.next_run_at ? formatDateTime(r.next_run_at) : "—"}</TD>
                <TD className="whitespace-nowrap">{formatDateTime(r.entered_at)}</TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}
      {q.hasNextPage && (
        <div className="border-t p-2">
          <Button size="sm" variant="ghost" onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage}>
            Show more
          </Button>
        </div>
      )}
    </Card>
  );
}

function Activity({ id }: { id: number }) {
  const q = useQuery({ queryKey: ["automation-activity", id], queryFn: () => api<{ entries: LogEntry[] }>(`/api/v1/automation-step-log` + qs({ automation_id: id, limit: 100 })) });
  const entries = q.data?.entries ?? [];
  return (
    <div className="space-y-1">
      <h3 className="text-sm font-semibold">What it did in the last 7 days</h3>
      {q.isLoading && <Skeleton className="h-24" />}
      {!q.isLoading && entries.length === 0 && (
        <Card>
          <Empty title="Nothing in the last 7 days." />
        </Card>
      )}
      {entries.length > 0 && <LogTable entries={entries} />}
    </div>
  );
}

function ShadowCompare({ id }: { id: number }) {
  const q = useQuery({ queryKey: ["automation-compare", id], queryFn: () => api<CompareOut>(`/api/v1/automations/${id}/shadow-compare`), retry: false });
  return (
    <Card className="overflow-x-auto">
      <CardHeader>
        <CardTitle>Shadow mode vs. Keap (last 14 days)</CardTitle>
      </CardHeader>
      {q.isLoading && <Skeleton className="m-3 h-16" />}
      {q.error && <p className="px-4 py-3 text-sm text-[var(--color-muted-foreground)]">{q.error.message}</p>}
      {q.data && (
        <>
          <CardContent className="flex flex-wrap gap-6 text-sm">
            <div>
              <div className="text-2xl font-semibold">{formatPercent(q.data.match_rate)}</div>
              <div className="text-xs text-[var(--color-muted-foreground)]">match</div>
            </div>
            <div>
              <div className="num text-lg">{formatInt(q.data.crm_would_send)}</div>
              <div className="text-xs text-[var(--color-muted-foreground)]">emails the CRM would have sent</div>
            </div>
            <div>
              <div className="num text-lg">{formatInt(q.data.keap_sent)}</div>
              <div className="text-xs text-[var(--color-muted-foreground)]">Keap actually sent</div>
            </div>
          </CardContent>
          {q.data.steps.length > 0 && (
            <Table>
              <THead>
                <tr>
                  <TH>Email step</TH>
                  <TH className="text-right">CRM</TH>
                  <TH className="text-right">Keap</TH>
                  <TH className="text-right">Both</TH>
                  <TH className="text-right">Only CRM</TH>
                  <TH className="text-right">Only Keap</TH>
                </tr>
              </THead>
              <tbody>
                {q.data.steps.map((s) => (
                  <TR key={s.step_key}>
                    <TD className="max-w-[14rem] truncate">{s.subject || s.step_key}</TD>
                    <TD className="num text-right">{s.crm_would_send}</TD>
                    <TD className="num text-right">{s.keap_sent}</TD>
                    <TD className="num text-right">{s.matched}</TD>
                    <TD className="num text-right">{s.crm_only}</TD>
                    <TD className="num text-right">{s.keap_only}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
          {(q.data.decisions ?? []).length > 0 && <Decisions decisions={q.data.decisions!} />}
          {q.data.notes.length > 0 && (
            <ul className="space-y-0.5 border-t px-4 py-2 text-xs text-[var(--color-muted-foreground)]">
              {q.data.notes.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}

/** Agree rate: 90%+ fine, 70-90% worth a look, below 70% the assumed Keap condition is probably wrong. */
function rateTone(d: CompareDecision): "success" | "warning" | "destructive" | "muted" {
  if (d.agree + d.disagree === 0) return "muted";
  if (d.agree_rate >= 0.9) return "success";
  if (d.agree_rate >= 0.7) return "warning";
  return "destructive";
}

/** Each decision step checked against Keap's history: did Keap send the emails of the way the CRM chose? */
function Decisions({ decisions }: { decisions: CompareDecision[] }) {
  return (
    <div className="border-t">
      <div className="px-4 pt-3 pb-1">
        <h3 className="text-sm font-semibold">Decisions checked against Keap</h3>
        <p className="text-xs text-[var(--color-muted-foreground)]">
          For each decision, did Keap send the emails of the way the CRM chose? A low agree rate means the condition assumed from the Keap campaign is probably wrong.
          "Can't tell" means neither way's emails show in Keap's history for that person.
        </p>
      </div>
      <Table>
        <THead>
          <tr>
            <TH>Decision</TH>
            <TH className="text-right">Agree rate</TH>
            <TH className="text-right">Agree</TH>
            <TH className="text-right">Disagree</TH>
            <TH className="text-right">Can't tell</TH>
            <TH>People where they disagree</TH>
          </tr>
        </THead>
        <tbody>
          {decisions.map((d) => (
            <TR key={d.step_key} className="align-top">
              <TD className="max-w-[18rem]">
                <div className="font-medium">{d.name || d.step_key}</div>
                <details className="text-xs text-[var(--color-muted-foreground)]">
                  <summary className="cursor-pointer select-none">
                    {d.ways.length} way{d.ways.length === 1 ? "" : "s"}, {formatInt(d.runs)} run{d.runs === 1 ? "" : "s"}
                  </summary>
                  <ul className="mt-1 space-y-1">
                    {d.ways.map((w) => (
                      <li key={w.name}>
                        <span className="font-medium text-[var(--color-foreground)]">{w.name}</span>
                        {w.next ? ` (then ${w.next})` : ""}: the CRM took it {formatInt(w.crm_took)} time{w.crm_took === 1 ? "" : "s"}, Keap agreed {formatInt(w.keap_agreed)}.
                        {w.subjects.length > 0 ? (
                          <span className="block">Told apart by: {w.subjects.map((x) => `“${x}”`).join(", ")}</span>
                        ) : (
                          <span className="block">Sends no email of its own, so Keap's history can't confirm it.</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </details>
              </TD>
              <TD className="text-right">
                <Badge variant={rateTone(d)}>{d.agree + d.disagree === 0 ? "n/a" : formatPercent(d.agree_rate)}</Badge>
              </TD>
              <TD className="num text-right">{formatInt(d.agree)}</TD>
              <TD className="num text-right">{formatInt(d.disagree)}</TD>
              <TD className="num text-right">{formatInt(d.unknown)}</TD>
              <TD className="max-w-[16rem]">
                {d.disagreements.length === 0 ? (
                  <span className="text-xs text-[var(--color-muted-foreground)]">none</span>
                ) : (
                  <span className="flex flex-wrap gap-x-2 gap-y-0.5 text-xs">
                    {d.disagreements.slice(0, 8).map((x) => (
                      <Link
                        key={`${x.contact_id}-${x.at}`}
                        to="/contacts/$contactId"
                        params={{ contactId: String(x.contact_id) }}
                        className="num text-[var(--color-primary)] hover:underline"
                        title={`CRM chose "${x.crm_way}", Keap went "${x.keap_way}" (${formatDateTime(x.at)})`}
                      >
                        #{x.contact_id}
                      </Link>
                    ))}
                    {d.disagreements.length > 8 && <span className="text-[var(--color-muted-foreground)]">+{d.disagreements.length - 8} more</span>}
                  </span>
                )}
              </TD>
            </TR>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function Versions({ d }: { d: Detail }) {
  return (
    <Card className="overflow-x-auto">
      <CardHeader>
        <CardTitle>Versions</CardTitle>
        {d.draft && <Badge variant="warning">unpublished draft v{d.draft.version}</Badge>}
      </CardHeader>
      <Table>
        <THead>
          <tr>
            <TH>Version</TH>
            <TH>Saved</TH>
            <TH>Published</TH>
            <TH className="text-right">Still running on it</TH>
            <TH>What changed</TH>
          </tr>
        </THead>
        <tbody>
          {d.versions.map((v) => (
            <TR key={v.id}>
              <TD className="num">v{v.version}</TD>
              <TD className="whitespace-nowrap" title={`by ${v.created_by}`}>
                {formatDateTime(v.created_at)}
              </TD>
              <TD className="whitespace-nowrap">{v.published_at ? formatDateTime(v.published_at) : "—"}</TD>
              <TD className="num text-right">{formatInt(v.live_count)}</TD>
              <TD className="max-w-[16rem] truncate text-xs">{v.notes ?? "—"}</TD>
            </TR>
          ))}
        </tbody>
      </Table>
      <p className="border-t px-4 py-2 text-xs text-[var(--color-muted-foreground)]">People stay on the version they started with. Claude edits steps through the MCP (automations_save, then publish).</p>
    </Card>
  );
}

type Action = { label: string; to: string; how: "publish" | "status"; tone: "default" | "destructive"; what: string };

function StatusActions({ a, hasDraft }: { a: AutomationSummary; hasDraft: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirm, setConfirm] = useState<Action | null>(null);
  const actions: Action[] = [];
  if (a.status === "draft" || hasDraft) actions.push({ label: hasDraft && a.status !== "draft" ? "Publish the draft in shadow mode" : "Start in shadow mode", to: "shadow", how: "publish", tone: "default", what: "It starts running as a simulation: every step is logged, nothing is sent or changed." });
  if (a.status === "shadow" || a.status === "paused") actions.push({ label: "Go live", to: "active", how: "status", tone: "destructive", what: "It starts running for real: emails go out and tags change for the people in it. The CRM first checks that its emails, tags and automations exist." });
  if (a.status === "active" || a.status === "shadow") actions.push({ label: "Pause", to: "paused", how: "status", tone: "default", what: "Nobody new starts and nobody moves until it's resumed." });
  if (a.status === "paused" || a.status === "active") actions.push({ label: a.status === "active" ? "Back to shadow mode" : "Resume in shadow mode", to: "shadow", how: "status", tone: "default", what: "It keeps running, but only logs what it would do." });
  if (a.status !== "archived" && a.status !== "draft") actions.push({ label: "Archive", to: "archived", how: "status", tone: "destructive", what: "Every run still going ends now and nobody new starts." });
  const run = useMutation({
    mutationFn: (x: Action) =>
      x.how === "publish" ? api(`/api/v1/automations/${a.id}/publish`, { json: { mode: x.to } }) : api(`/api/v1/automations/${a.id}/status`, { json: { status: x.to } }),
    onSuccess: (_r, x) => {
      void qc.invalidateQueries({ queryKey: ["automation", a.id] });
      void qc.invalidateQueries({ queryKey: ["automations-list"] });
      toast(`${a.name}: ${x.label.toLowerCase()} done`);
      setConfirm(null);
    },
    onError: (e) => toast(e.message, "error"),
  });
  return (
    <span className="ml-auto flex flex-wrap gap-1.5">
      {actions.map((x) => (
        <Button key={x.label} size="sm" variant="outline" className={cn(x.tone === "destructive" && "text-[var(--color-destructive)]")} onClick={() => setConfirm(x)}>
          {x.label}
        </Button>
      ))}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent>
          <DialogTitle>
            {confirm?.label}: {a.name}?
          </DialogTitle>
          <DialogDescription>{confirm?.what}</DialogDescription>
          {confirm?.to === "active" && (
            <p className="flex items-start gap-2 rounded-md bg-[var(--color-warning-bg)] px-3 py-2 text-xs text-[var(--color-warning-fg)]">
              <ShieldAlert className="mt-0.5 size-3.5 shrink-0" /> Check the shadow comparison first. People partway through the matching Keap campaign should finish in Keap, so turn off
              new entrants there at the same time.
            </p>
          )}
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirm(null)}>
              Not now
            </Button>
            <Button variant={confirm?.tone === "destructive" ? "destructive" : "default"} disabled={run.isPending} onClick={() => confirm && run.mutate(confirm)}>
              {confirm?.label}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </span>
  );
}
